import React from 'react';
import { TableRow as MuiTableRow, TableCell as MuiTableCell, IconButton, styled, Collapse, Box, Typography, Table as MuiTable, TableHead, TableBody, Chip, Tooltip, Checkbox, Stack, Popover, Button } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { formatCellValue, renderAuthStatus, renderCanceladoERP, getColumnType, renderEstatusChip, renderStatusColor, renderStatusBorderColor, getRowKey } from '../utils/formatters';
import { CellText } from './TableCell';
import { AdminPaymentStatusCell } from './AdminPaymentStatusCell';

const StyledTableRow = styled(MuiTableRow, {
  shouldForwardProp: (prop) => prop !== 'bgColor' && prop !== 'statusBorderColor' && prop !== 'underlineColor'
})(({ theme, bgColor, statusBorderColor, underlineColor }) => {
  const baseBg = bgColor || '#ffffff';

  const bgStyle = underlineColor
    ? `linear-gradient(270deg, ${underlineColor}2d 0%, ${underlineColor}08 30%, transparent 45%)`
    : 'none';

  return {
    transition: 'background-color 0.1s ease',
    position: 'relative',
    backgroundColor: baseBg,
    contentVisibility: 'auto',
    containIntrinsicSize: 'auto 38px',
    contain: 'layout style paint',
    willChange: 'transform, background-color',
    ...(underlineColor ? { background: bgStyle } : {}),

    transform: 'translate3d(0, 0, 0)',
    backfaceVisibility: 'hidden',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',

    ...(statusBorderColor && {
      boxShadow: `inset 4px 0 0 ${statusBorderColor}`,
      '& > td:first-of-type': {
        boxShadow: `inset 4px 0 0 ${statusBorderColor}`,
      }
    }),
    '&:nth-of-type(even)': {
      backgroundColor: bgColor && bgColor !== '#ffffff' ? bgColor : '#f8fafc',
      ...(underlineColor ? {
        background: `linear-gradient(270deg, ${underlineColor}2d 0%, ${underlineColor}08 30%, transparent 45%)`
      } : {}),
    },
    '&:hover': {
      backgroundColor: bgColor && bgColor !== '#ffffff' ? bgColor : '#eff6ff !important',
      boxShadow: '0 4px 12px rgba(14, 165, 233, 0.08)',
      zIndex: 2,
      '& > td': {
         backgroundColor: bgColor && bgColor !== '#ffffff' ? bgColor : '#eff6ff !important',
         borderColor: '#bfdbfe',
      },
      '& > td:first-of-type': {
        boxShadow: statusBorderColor ? `inset 4px 0 0 ${statusBorderColor}` : 'inset 4px 0 0 #2563eb', 
      }
    },
    '& > td': {
      borderBottom: `1px solid #e2e8f0`,
      padding: 'clamp(4px, 0.45vw, 7px) clamp(6px, 0.6vw, 10px)',
      fontSize: 'clamp(0.64rem, 0.60rem + 0.1vw, 0.74rem)',
      backgroundColor: 'inherit',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
    }
  };
});

const stickyCellSx = (meta) => {
  if (!meta) return {};
  const isPinned = meta.isPinned;
  return {
    position: 'sticky',
    left: meta.left,
    zIndex: meta.zIndex,
    minWidth: meta.width,
    width: meta.width,
    maxWidth: meta.width,
    backgroundColor: 'inherit', // Solid color from row
    boxShadow: meta.isLastSticky 
      ? '6px 0 15px -3px rgba(0,0,0,0.12)' 
      : (isPinned ? 'inset -1px 0 0 rgba(0,0,0,0.015)' : 'none'),
    borderRight: isPinned ? '1px solid rgba(0,0,0,0.04)' : 'none',
  };
};

const pickFirst = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== null && value !== undefined && String(value).trim() !== '') return value;
  }
  return '';
};

const normalizeFacturaValue = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';

  const normalized = text.toUpperCase();
  if (['N/A', 'NA', 'S/N', 'SN', 'SIN FOLIO', 'PENDIENTE', 'NULL', 'UNDEFINED'].includes(normalized)) {
    return '';
  }

  const parts = text.split(',').map(part => part.trim()).filter(Boolean);
  const validParts = parts.filter((part) => {
    const clean = part.trim();
    if (/^-?\d+(\.0+)?$/.test(clean)) {
      return Number(clean) > 0;
    }
    return !clean.startsWith('-');
  });

  return validParts.join(', ');
};

const pickFacturaValue = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    const normalized = normalizeFacturaValue(value);
    if (normalized) return normalized;
  }
  return '';
};

const summarizeFactura = (value, emptyLabel = 'Pendiente') => {
  const shorten = (part) => {
    const clean = String(part || '').trim();
    if (clean.length <= 22) return clean;
    return `${clean.slice(0, 8)}...${clean.slice(-8)}`;
  };

  const text = normalizeFacturaValue(value);
  if (!text) return { display: emptyLabel, full: '', parts: [], canOpen: false };
  const parts = text.split(',').map(part => part.trim()).filter(Boolean);
  const hasLongPart = parts.some(part => part.length > 22);

  if (parts.length <= 2) {
    return {
      display: parts.map(shorten).join(', '),
      full: text,
      parts,
      canOpen: hasLongPart || parts.length > 1,
    };
  }

  return {
    display: `${parts.slice(0, 2).map(shorten).join(', ')} +${parts.length - 2} mas`,
    full: text,
    parts,
    canOpen: true,
  };
};

const getEmpresaChipSx = (empresa = '') => {
  const normalized = String(empresa).trim().toUpperCase();

  if (normalized.includes('OFFSHORE')) {
    return {
      bgcolor: '#eef6ff',
      color: '#005eb8',
      borderColor: '#cfe6ff',
    };
  }

  if (normalized.includes('PESADO')) {
    return {
      bgcolor: '#f7ead8',
      color: '#7c3f00',
      borderColor: '#e6c79f',
      fontWeight: 950,
    };
  }

  if (normalized.includes('ECO') || normalized.includes('CONSTRUCC')) {
    return {
      bgcolor: '#f4eee7',
      color: '#6b3f1f',
      borderColor: '#dfcbb9',
      fontWeight: 900,
    };
  }

  return {
    bgcolor: '#eef2f7',
    color: '#334155',
    borderColor: '#d9e2ec',
  };
};

const getEmpresaComplementChipSx = (empresa = '') => {
  const normalized = String(empresa).trim().toUpperCase();

  if (normalized.includes('OFFSHORE')) {
    return {
      bgcolor: '#e6f0fa',
      color: '#005eb8',
      borderColor: '#b8daff',
    };
  }

  if (normalized.includes('PESADO')) {
    return {
      bgcolor: '#fcefdc',
      color: '#6c3400',
      borderColor: '#f5d3a5',
    };
  }

  if (normalized.includes('ECO') || normalized.includes('CONSTRUCC')) {
    return {
      bgcolor: '#f5ebd9',
      color: '#5c3317',
      borderColor: '#e2ccb2',
    };
  }

  return {
    bgcolor: '#eef2f7',
    color: '#334155',
    borderColor: '#d9e2ec',
  };
};

const getEmpresaChipLabel = (empresa = '') => {
  const normalized = String(empresa).trim().toUpperCase();

  if (normalized.includes('OFFSHORE')) return 'OFFSHORE';
  if (normalized.includes('PESADO')) return 'PESADO';
  if (normalized.includes('CONSTRUCC')) return 'CONSTRUCCIONES';
  if (normalized.includes('ECO')) return 'ECO';
  return String(empresa || '').trim();
};

const DetailValue = ({ label, value, wide = false }) => (
  <Box sx={{ minWidth: 0, gridColumn: wide ? { xs: 'span 2', md: 'span 2', lg: 'span 2', xl: 'span 3' } : 'auto' }}>
    <Typography sx={{ fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#7b8da8', letterSpacing: 0.2, fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography sx={{ mt: 0.15, fontSize: 11, color: '#0f172a', fontWeight: 900, lineHeight: 1.18, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {value || '-'}
    </Typography>
  </Box>
);

const CompactInfoCell = ({ primary, secondary, chip, title, secondaryTitle, chipSx, maxWidth = '100%' }) => {
  const primaryEl = (
    <Typography
      sx={{
        fontSize: 'clamp(0.62rem, 0.58rem + 0.1vw, 0.74rem)',
        color: '#0f172a',
        fontWeight: 650,
        lineHeight: 1.15,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: maxWidth || '100%',
      }}
    >
      {primary || '-'}
    </Typography>
  );

  const secondaryText = secondaryTitle || secondary || '';
  const secondaryEl = secondary ? (
    <Typography
      sx={{
        mt: 0.05,
        fontSize: 'clamp(0.54rem, 0.50rem + 0.08vw, 0.64rem)',
        color: '#64748b',
        fontWeight: 500,
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: maxWidth || '100%',
      }}
    >
      {secondary}
    </Typography>
  ) : null;

  return (
    <Box sx={{ minWidth: 0, maxWidth }}>
      {title ? (
        <Tooltip title={title} arrow disableInteractive>
          <span>{primaryEl}</span>
        </Tooltip>
      ) : primaryEl}

      {secondaryEl && (
        secondaryText ? (
          <Tooltip title={secondaryText} arrow disableInteractive>
            <span>{secondaryEl}</span>
          </Tooltip>
        ) : secondaryEl
      )}

      {chip && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            mt: 0.15,
            px: 0.4,
            py: 0,
            borderRadius: '3px',
            border: '1px solid transparent',
            bgcolor: '#eef6ff',
            color: '#005eb8',
            fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)',
            fontWeight: 700,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.2,
            ...chipSx,
          }}
        >
          {chip}
        </Box>
      )}
    </Box>
  );
};

const NotaRequiChip = ({ nota, empresa }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const cleanNota = String(nota || '').trim();
  if (!cleanNota) return null;

  const open = Boolean(anchorEl);
  const complementSx = getEmpresaComplementChipSx(empresa);

  return (
    <>
      <Chip
        label="Nota"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        sx={{
          height: 18,
          ml: 0.6,
          bgcolor: complementSx.bgcolor,
          color: complementSx.color,
          border: `1px solid ${complementSx.borderColor}`,
          fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
          fontWeight: 900,
          cursor: 'pointer',
          '& .MuiChip-label': { px: 0.7 },
          '&:hover': { opacity: 0.85 },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 260,
              p: 1.25,
              borderRadius: 2,
              border: '1px solid #dbeafe',
              boxShadow: '0 12px 30px rgba(15,23,42,0.14)',
            },
          },
        }}
      >
        <Typography sx={{ mb: 0.75, color: '#0655A5', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
          Notas Requi
        </Typography>
        <Typography sx={{ color: '#0f172a', fontSize: 12.5, fontWeight: 650, lineHeight: 1.35, whiteSpace: 'pre-wrap' }}>
          {cleanNota}
        </Typography>
      </Popover>
    </>
  );
};

const FacturaEconomicoCell = ({ row }) => {
  const factura = pickFacturaValue(row, ['FOLIO_FACTURA', 'FOLIO_FACT', 'FACTURA', 'UUID']);
  const economico = pickFirst(row, ['NUMERO_ECONOMICO', 'ECONOMICO']) || 'S/N';
  const facturaSummary = summarizeFactura(factura, 'Pendiente');

  return (
    <Box sx={{ minWidth: 0, maxWidth: { xs: 170, md: 220, lg: 260 } }}>
      <FacturaCompactCell value={factura} pago="" emptyLabel="Pendiente" />
      <Tooltip title={`Num. Eco: ${economico}`} arrow disableInteractive>
        <Typography
          sx={{
            mt: 0.2,
            color: '#64748b',
            fontSize: 9.6,
            fontWeight: 800,
            lineHeight: 1.05,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Num. Eco: {economico}
        </Typography>
      </Tooltip>
      {facturaSummary.parts.length > 2 && (
        <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: 8.8, fontWeight: 800, lineHeight: 1 }}>
          {facturaSummary.parts.length} facturas
        </Typography>
      )}
    </Box>
  );
};

const TotalDivisaCell = ({ row }) => {
  const divisa = pickFirst(row, ['DIVISA', 'MONEDA']) || 'MXN';
  const val = pickFirst(row, ['TOTAL_OC', 'TOTAL', 'TOTALOC', 'MONTO', 'MONTO_SOLICITADO', 'MONTOSOLICITADO']);
  const total = formatCellValue('TOTAL', val);

  return (
    <Box sx={{ minWidth: 0, py: 0.1 }}>
      <Typography
        sx={{
          fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)',
          color: '#0f172a',
          fontWeight: 650,
          lineHeight: 1.15,
          whiteSpace: 'nowrap',
        }}
      >
        {total || '$0.00'}
      </Typography>
      <Typography
        sx={{
          mt: 0.05,
          fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
          color: '#64748b',
          fontWeight: 400,
          lineHeight: 1.05,
          whiteSpace: 'nowrap',
        }}
      >
        {divisa}
      </Typography>
    </Box>
  );
};

const PagosAutOcEmpresaCell = ({ row }) => {
  const folio = pickFirst(row, ['FOLIO', 'FOLIO_OC', 'ORDEN']);
  const requi = pickFirst(row, ['REQUI', 'REQUISICION']);
  const empresa = pickFirst(row, ['EMPRESA', 'NOMBRE_EMPRESA']);
  const veces = Number(pickFirst(row, ['VECES_REPORTADO', 'vecesReportado', 'REPORTE_SOLICITADO_COUNT', 'VECES']) || 0);

  const empresaChip = empresa ? getEmpresaChipLabel(empresa) : '';
  const empresaSx = getEmpresaComplementChipSx(empresa);

  return (
    <Box sx={{ minWidth: 0, py: 0.2, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
      {/* 1. Folio / Orden */}
      <Typography
        sx={{
          fontSize: 'clamp(0.64rem, 0.60rem + 0.1vw, 0.76rem)',
          color: '#0f172a',
          fontWeight: 800,
          lineHeight: 1.15,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={folio}
      >
        {folio || '-'}
      </Typography>

      {/* 2. Requisición si existe */}
      {requi && (
        <Typography
          sx={{
            fontSize: 'clamp(0.52rem, 0.48rem + 0.08vw, 0.62rem)',
            color: '#64748b',
            fontWeight: 500,
            lineHeight: 1.05,
            whiteSpace: 'nowrap',
          }}
        >
          Req: {requi}
        </Typography>
      )}

      {/* 3. Empresa Chip */}
      {empresaChip && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            px: 0.5,
            py: 0.05,
            borderRadius: '4px',
            border: '1px solid transparent',
            bgcolor: '#eef6ff',
            color: '#005eb8',
            fontSize: 'clamp(0.54rem, 0.50rem + 0.08vw, 0.66rem)',
            fontWeight: 800,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.2,
            ...empresaSx,
          }}
        >
          {empresaChip}
        </Box>
      )}

      {/* 4. Badge Solicitada Debajo de Empresa */}
      {veces > 0 && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            alignItems: 'center',
            px: 0.6,
            py: 0.15,
            borderRadius: '4px',
            fontSize: '0.58rem',
            fontWeight: 800,
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            ...(veces >= 2 ? {
              bgcolor: '#ea580c',
              color: '#ffffff',
              boxShadow: '0 1px 2px rgba(234, 88, 12, 0.2)'
            } : {
              bgcolor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #cbd5e1'
            })
          }}
        >
          {`Solicitada ${veces} ${veces === 1 ? 'vez' : 'veces'}`}
        </Box>
      )}
    </Box>
  );
};

const PagosAutCentroCell = ({ row }) => {
  const numero = pickFirst(row, ['NUMEROCC', 'NUMERO_CC']);
  const centro = pickFirst(row, ['CENTROCOSTO', 'NOMBRE_CC']);
  const departamento = pickFirst(row, ['DEPARTAMENTO']);
  const empresa = pickFirst(row, ['EMPRESA', 'NOMBRE_EMPRESA']);
  const empresaChipSx = getEmpresaComplementChipSx(empresa);

  return (
    <CompactInfoCell
      primary={centro || numero}
      secondary={`Depto: ${departamento || '-'}`}
      chip={numero}
      chipSx={empresaChipSx}
      title={[numero, centro, departamento].filter(Boolean).join(' | ')}
      maxWidth={{ xs: 180, md: 230, lg: 300 }}
    />
  );
};

const PagosAutCompradorFechaCell = ({ row }) => {
  const comprador = pickFirst(row, ['COMPRADOR', 'SOLICITANTE']);

  // Fecha de la Solicitud de Pago (cuando el comprador la generó)
  const fechaSolicitud = formatCellValue(
    'FECHASOLICITUD',
    pickFirst(row, ['FECHASOLICITUD', 'FECHA_SOLICITUD', 'FECHA_AUTSOLPAGO', 'FECHA_CREACION'])
  );

  // Fecha de la Orden de Compra original en el ERP
  const fechaOC = formatCellValue(
    'FECHA',
    pickFirst(row, ['FECHA_OC', 'FECHA_ORDENCOMPRA', 'FECHAOC', 'FECHA'])
  );

  const dateSx = {
    mt: 0.1,
    fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  };

  return (
    <Box sx={{ minWidth: 0, maxWidth: { xs: 150, md: 190, lg: 230 } }}>
      <Tooltip title={comprador || ''} arrow disableInteractive>
        <Typography
          sx={{
            fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)',
            color: '#0f172a',
            fontWeight: 650,
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {comprador || '-'}
        </Typography>
      </Tooltip>
      <Typography sx={{ ...dateSx, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#0655a5' }}>
        Sol: {fechaSolicitud || '-'}
      </Typography>
      <Typography sx={{ ...dateSx, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#64748b' }}>
        OC: {fechaOC || '-'}
      </Typography>
    </Box>
  );
};

const PagosAutImporteCell = ({ row }) => (
  <CompactInfoCell
    primary={formatCellValue('MONTOSOLICITADO', pickFirst(row, ['MONTOSOLICITADO', 'MONTO_SOLICITADO']))}
    maxWidth={120}
  />
);

const pickAuthDate = (row, index) => {
  const date = pickFirst(row, [
    `AUT${index}_FECHA`,
    `AUTNIV${index}_FECHA`,
    `FECHA_AUT${index}`,
    `FECHA_AUTORIZACION${index}`,
  ]);
  return date ? formatCellValue('FECHASOLICITUD', date) : '';
};

const PagosAutSeguimientoCell = ({ row }) => {
  const items = [];

  for (let i = 1; i <= 8; i++) {
    const name = pickFirst(row, [`AUT${i}`, `AUTNIV${i}_NOMBRE`, `AUTORIZADOR${i}`]);
    const status = pickFirst(row, [`AUTNIV${i}`, `AUTNIVEL${i}`]);
    const normalizedName = String(name || '').trim();
    const normalizedStatus = String(status || '').trim();

    if (!normalizedName && !normalizedStatus) continue;
    if (normalizedName === 'N/A' && normalizedStatus === 'N/A') continue;

    items.push({
      level: i,
      name: normalizedName || `Nivel ${i}`,
      status: normalizedStatus || 'N/A',
      date: pickAuthDate(row, i),
    });
  }

  if (!items.length) {
    return <Typography sx={{ color: '#64748b', fontSize: 11, fontWeight: 800 }}>N/A</Typography>;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(items.length, 4)}, minmax(42px, 1fr))`,
        gap: 0.55,
        alignItems: 'center',
        minWidth: 0,
        width: { xs: 170, md: 210, lg: 260 },
      }}
    >
      {items.slice(0, 4).map((item) => {
        const tooltip = [
          `Nivel ${item.level}: ${item.name}`,
          item.status === '1' ? 'Autorizado' : (item.status === '0' ? 'Pendiente' : item.status),
          item.date ? `Fecha: ${item.date}` : '',
        ].filter(Boolean).join('\n');

        return (
          <Tooltip key={`${item.level}-${item.name}`} title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>} arrow disableInteractive>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, lineHeight: 1 }}>
              <Typography
                sx={{
                  maxWidth: 58,
                  color: '#475569',
                  fontSize: 8.8,
                  fontWeight: 850,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.name}
              </Typography>
              <Box sx={{ mt: 0.12, lineHeight: 0, '& svg': { fontSize: 16 } }}>
                {renderAuthStatus(item.status, row.MOTIVOCANCELACION)}
              </Box>
              {item.date && (
                <Typography sx={{ mt: 0.1, color: '#64748b', fontSize: 8.3, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>
                  {item.date}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}
      {items.length > 4 && (
        <Typography sx={{ color: '#64748b', fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', fontWeight: 900, alignSelf: 'center' }}>
          +{items.length - 4}
        </Typography>
      )}
    </Box>
  );
};

const SolicitudSaldoCell = ({ row }) => {
  const solicitado = formatCellValue('MONTOSOLICITADO', pickFirst(row, ['MONTOSOLICITADO', 'MONTO_SOLICITADO', 'SOLICITADO']));
  const pendiente = formatCellValue('PENDIENTE', pickFirst(row, ['PENDIENTE', 'RESTANTE']));
  const pagado = formatCellValue('PAGADO', pickFirst(row, ['PAGADO', 'PAGO_ACUM', 'ABONOS']));

  return (
    <Box sx={{ minWidth: 0, width: { xs: 142, md: 160, lg: 176 } }}>
      <Tooltip
        title={`Solicitado: ${solicitado}\nPendiente: ${pendiente}\nPagado: ${pagado}`}
        arrow
        disableInteractive
      >
        <Box
          sx={{
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            columnGap: 0.75,
            alignItems: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          <Box sx={{ minWidth: 0, display: 'grid', rowGap: 0.12 }}>
            <Typography
              sx={{
                color: '#0f172a',
                fontSize: 'clamp(0.56rem, 0.54rem + 0.08vw, 0.68rem)',
                fontWeight: 900,
                lineHeight: 1.05,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Box component="span" sx={{ color: '#64748b', fontSize: 8.4, fontWeight: 850, mr: 0.25 }}>
                Sol.
              </Box>
              {solicitado}
            </Typography>
            <Typography
              sx={{
                color: '#15803d',
                fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                fontWeight: 900,
                lineHeight: 1.05,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Box component="span" sx={{ color: '#64748b', fontSize: 8.4, fontWeight: 850, mr: 0.25 }}>
                Pag.
              </Box>
              {pagado}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#64748b', fontSize: 8.4, fontWeight: 850, lineHeight: 1, textTransform: 'uppercase' }}>
              Rest.
            </Typography>
            <Typography
              sx={{
                mt: 0.08,
                color: '#b91c1c',
                fontSize: 'clamp(0.56rem, 0.54rem + 0.08vw, 0.68rem)',
                fontWeight: 930,
                lineHeight: 1.05,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {pendiente}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};



const FacturaCompactCell = ({ value, pago, emptyLabel = 'Pendiente' }) => {
  const [expanded, setExpanded] = React.useState(false);
  const factura = summarizeFactura(value, emptyLabel);
  const canOpen = factura.canOpen;

  const handleToggle = (event) => {
    event.stopPropagation();
    if (canOpen) setExpanded((current) => !current);
  };

  return (
    <Box sx={{ minWidth: 0, maxWidth: 160 }}>
      <Typography
        onClick={handleToggle}
        sx={{
          fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)',
          color: '#0f172a',
          fontWeight: 650,
          lineHeight: 1.15,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
          cursor: canOpen ? 'pointer' : 'default',
          textDecoration: canOpen ? 'underline' : 'none',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '2px',
        }}
      >
        {factura.display}
      </Typography>
      {canOpen && (
        <Typography
          component="button"
          type="button"
          onClick={handleToggle}
          sx={{
            mt: 0.05,
            p: 0,
            border: 0,
            bgcolor: 'transparent',
            color: '#005eb8',
            fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
            fontWeight: 500,
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          {expanded ? 'Ocultar facturas' : 'Ver facturas'}
        </Typography>
      )}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            mt: 0.45,
            maxHeight: 132,
            overflow: 'auto',
            pr: 0.35,
            display: 'grid',
            gap: 0.35,
          }}
        >
          {(factura.parts.length ? factura.parts : [factura.display]).map((item, index) => (
            <Box
              key={`${item}-${index}`}
              sx={{
                px: 0.55,
                py: 0.35,
                borderRadius: '6px',
                bgcolor: index % 2 === 0 ? '#f8fafc' : '#eef6ff',
                color: '#0f172a',
                fontSize: 11,
                fontWeight: 800,
                lineHeight: 1.2,
                wordBreak: 'break-word',
                userSelect: 'text',
              }}
            >
              {item}
            </Box>
          ))}
        </Box>
      </Collapse>
      {pago && (
        <Typography sx={{ mt: 0.2, fontSize: 'clamp(0.56rem, 0.54rem + 0.08vw, 0.68rem)', color: '#64748b', fontWeight: 700, lineHeight: 1.15 }}>
          Pago: {pago}
        </Typography>
      )}
    </Box>
  );
};

const PartidasDetailPanel = ({ row, labels }) => {
  const [activeTab, setActiveTab] = React.useState(0);
  const partidas = Array.isArray(row.partidas) ? row.partidas : [];
  const folio = pickFirst(row, ['FOLIO', 'FOLIO_OC', 'ORDEN', 'ID']);
  const notas = pickFirst(row, ['NOTA', 'NOTAS', 'OBSERVACIONES', 'CONCEPTOPAGO']);
  const cadenaAut = Array.isArray(row.cadenaAut) ? row.cadenaAut : [];

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 0,
        bgcolor: '#f8fafc',
        mx: 0,
        my: 0.65,
        px: 0.75,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: 'minmax(360px, 0.82fr) minmax(0, 1.7fr)',
          },
          gap: 0.75,
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            bgcolor: '#f8fafc',
            border: '1px solid #dbe3ee',
            borderRadius: '8px',
            boxShadow: '0 3px 10px rgba(15, 23, 42, 0.04)',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <Box sx={{ px: 1, py: 0.55, borderBottom: '1px solid #e6edf6' }}>
            <Typography sx={{ color: '#005eb8', fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.35 }}>
              Informacion general OC
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
                lg: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(3, minmax(0, 1fr))',
              },
              columnGap: 1.25,
              rowGap: 0.6,
            }}
          >
            <DetailValue label="Folio OC" value={folio} />
            <DetailValue label="Requi" value={pickFirst(row, ['REQUI', 'REQUISICION'])} />
            <DetailValue label="Fecha OC" value={formatCellValue('FECHA', pickFirst(row, ['FECHA', 'FECHA_OC', 'FECHASOLICITUD', 'FECHA_SOLICITUD', 'FECHA_EMISION', 'FECHA_CREACION', 'FECHACREACION', 'FECHA_REQUISICION']))} />
            <DetailValue label="Empresa" value={pickFirst(row, ['EMPRESA', 'NOMBRE_EMPRESA'])} />
            <DetailValue label="Proveedor" value={pickFirst(row, ['PROVEEDOR', 'PROVEEDOR_NOMBRE', 'NOMBRE_PROVEEDOR'])} wide />
            <DetailValue label="Centro costo" value={pickFirst(row, ['CENTROCOSTO', 'CENTRO_COSTO', 'CENTROCOSTOS', 'NOMBRE_CC', 'NUMEROCC', 'NUMERO_CC'])} wide />
            <DetailValue label="Departamento" value={pickFirst(row, ['DEPARTAMENTO'])} />
            <DetailValue label="Comprador" value={pickFirst(row, ['COMPRADOR', 'SOLICITANTE', 'COMPRADORA'])} />
            <DetailValue label="Requisitor" value={pickFirst(row, ['REQUISITOR', 'SOLICITANTE'])} />
            <DetailValue label="Condicion" value={pickFirst(row, ['PAGO', 'CONDICIONPAGO', 'CONDICION', 'METODOPAGO', 'CONCEPTOPAGO'])} />
            <DetailValue label="Divisa" value={pickFirst(row, ['DIVISA', 'MONEDA'])} />
            <DetailValue label="Estatus" value={pickFirst(row, ['ESTATUS'])} />
            <DetailValue label="Factura" value={pickFirst(row, ['FOLIO_FACTURA', 'FACTURA', 'FACTURA_FOLIO', 'NUM_FACTURA'])} />
            <DetailValue label="Vencimiento" value={formatCellValue('FECHA', pickFirst(row, ['VENCIMIENTO', 'FECHA_VENCIMIENTO', 'FECHAVENCIMIENTO', 'FECHA_VENCIMIENTO_REAL']))} />
            <DetailValue label="Pendiente" value={formatCellValue('PENDIENTE', pickFirst(row, ['PENDIENTE', 'RESTANTE', 'MONTO_PENDIENTE']))} />
          </Box>

          {notas && (
            <Box sx={{ mx: 1, mb: 1, p: 0.85, bgcolor: '#f1f5f9', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
              <Typography sx={{ mb: 0.3, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#475569', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                📝 Notas / Observaciones
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  color: '#0f172a',
                  fontWeight: 600,
                  lineHeight: 1.35,
                  maxHeight: 75,
                  overflowY: 'auto',
                  pr: 0.5,
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  fontFamily: 'inherit',
                }}
              >
                {notas}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            bgcolor: '#ffffff',
            border: '1px solid #dbe3ee',
            borderRadius: '8px',
            boxShadow: '0 3px 10px rgba(15, 23, 42, 0.04)',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {/* Header con Pestañas */}
          <Box sx={{ px: 1, pt: 0.4, borderBottom: '1px solid #e6edf6', bgcolor: '#ffffff', display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={() => setActiveTab(0)}
              sx={{
                fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)',
                fontWeight: 900,
                color: activeTab === 0 ? '#005eb8' : '#64748b',
                borderBottom: activeTab === 0 ? '2px solid #005eb8' : '2px solid transparent',
                borderRadius: 0,
                px: 1, py: 0.3, minWidth: 0, textTransform: 'none'
              }}
            >
              📦 Partidas OC ({partidas.length})
            </Button>
            <Button
              size="small"
              onClick={() => setActiveTab(1)}
              sx={{
                fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)',
                fontWeight: 900,
                color: activeTab === 1 ? '#005eb8' : '#64748b',
                borderBottom: activeTab === 1 ? '2px solid #005eb8' : '2px solid transparent',
                borderRadius: 0,
                px: 1, py: 0.3, minWidth: 0, textTransform: 'none'
              }}
            >
              📄 Solicitud e Historial {cadenaAut.length > 0 && `(${cadenaAut.length})`}
            </Button>
          </Box>

          {/* Contenido Pestaña 0: Partidas */}
          {activeTab === 0 && (
            partidas.length ? (
              <Box sx={{ maxHeight: 178, overflow: 'auto' }}>
                <MuiTable size="small" sx={{ '& th, & td': { borderBottom: '1px solid #edf2f7', py: 0.42, px: 0.85 } }}>
                  <TableHead>
                    <MuiTableRow>
                      <MuiTableCell sx={{ width: 32, color: '#8a9ab5', fontWeight: 800, fontSize: 11, bgcolor: '#ffffff' }}>#</MuiTableCell>
                      <MuiTableCell sx={{ color: '#8a9ab5', fontWeight: 800, fontSize: 11, bgcolor: '#ffffff' }}>Codigo</MuiTableCell>
                      <MuiTableCell sx={{ color: '#8a9ab5', fontWeight: 800, fontSize: 11, bgcolor: '#ffffff' }}>Descripcion</MuiTableCell>
                      <MuiTableCell align="right" sx={{ color: '#8a9ab5', fontWeight: 800, fontSize: 11, bgcolor: '#ffffff' }}>Cant.</MuiTableCell>
                      <MuiTableCell align="right" sx={{ color: '#8a9ab5', fontWeight: 800, fontSize: 11, bgcolor: '#ffffff' }}>Costo U.</MuiTableCell>
                      <MuiTableCell align="right" sx={{ color: '#8a9ab5', fontWeight: 800, fontSize: 11, bgcolor: '#ffffff' }}>Total</MuiTableCell>
                    </MuiTableRow>
                  </TableHead>
                  <TableBody>
                    {partidas.map((partida, idx) => (
                      <MuiTableRow key={idx} hover>
                        <MuiTableCell sx={{ color: '#64748b', fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)' }}>{partida.CONSECUTIVO || idx + 1}</MuiTableCell>
                        <MuiTableCell sx={{ color: '#0f172a', fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)', fontWeight: 800 }}>
                          {pickFirst(partida, ['CODIGO_PART', 'CODIGO', 'CODIGO_PARTIDA']) || '-'}
                        </MuiTableCell>
                        <MuiTableCell sx={{ color: '#0f172a', fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)', fontWeight: 650 }}>
                          {pickFirst(partida, ['DESCRIPCION_PART', 'DESCRIPCION']) || '-'}
                        </MuiTableCell>
                        <MuiTableCell align="right" sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)' }}>{formatCellValue('CANTIDAD', partida.CANTIDAD)}</MuiTableCell>
                        <MuiTableCell align="right" sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)' }}>{formatCellValue('COSTOU', partida.COSTOU)}</MuiTableCell>
                        <MuiTableCell align="right" sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)', fontWeight: 900 }}>{formatCellValue('TOTAL_PART', partida.TOTAL_PART)}</MuiTableCell>
                      </MuiTableRow>
                    ))}
                  </TableBody>
                </MuiTable>
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  No hay detalles de partidas disponibles para esta orden.
                </Typography>
              </Box>
            )
          )}

          {/* Contenido Pestaña 1: Solicitud e Historial */}
          {activeTab === 1 && (
            <Box sx={{ p: 1, maxHeight: 178, overflow: 'auto' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 1.5, bgcolor: '#f8fafc', p: 1, borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <Box>
                  <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>ID SOLICITUD</Typography>
                  <Typography sx={{ fontSize: 12, color: '#0f172a', fontWeight: 800 }}>#{row.ID || '-'}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>MONTO SOLICITADO</Typography>
                  <Typography sx={{ fontSize: 12, color: '#16a34a', fontWeight: 800 }}>{formatCellValue('MONTOSOLICITADO', row.MONTOSOLICITADO)}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>FECHA SOLICITUD</Typography>
                  <Typography sx={{ fontSize: 12, color: '#0f172a', fontWeight: 800 }}>{formatCellValue('FECHA', row.FECHA_SOLICITUD || row.FECHASOLICITUD)}</Typography>
                </Box>
              </Box>

              <Typography sx={{ fontSize: 11, color: '#334155', fontWeight: 800, mb: 0.5 }}>
                Cadena de Autorización e Historial
              </Typography>
              {cadenaAut.length ? (
                <MuiTable size="small" sx={{ '& th, & td': { borderBottom: '1px solid #edf2f7', py: 0.3, px: 0.6 } }}>
                  <TableHead>
                    <MuiTableRow>
                      <MuiTableCell sx={{ color: '#8a9ab5', fontWeight: 800, fontSize: 10, bgcolor: '#ffffff' }}>Rol</MuiTableCell>
                      <MuiTableCell sx={{ color: '#8a9ab5', fontWeight: 800, fontSize: 10, bgcolor: '#ffffff' }}>Usuario</MuiTableCell>
                      <MuiTableCell sx={{ color: '#8a9ab5', fontWeight: 800, fontSize: 10, bgcolor: '#ffffff' }}>Estado</MuiTableCell>
                      <MuiTableCell sx={{ color: '#8a9ab5', fontWeight: 800, fontSize: 10, bgcolor: '#ffffff' }}>Fecha Evento</MuiTableCell>
                    </MuiTableRow>
                  </TableHead>
                  <TableBody>
                    {cadenaAut.map((aut, idx) => (
                      <MuiTableRow key={idx}>
                        <MuiTableCell sx={{ fontSize: 11, fontWeight: 700 }}>{aut.ROL || '-'}</MuiTableCell>
                        <MuiTableCell sx={{ fontSize: 11 }}>{aut.USUARIO || '-'}</MuiTableCell>
                        <MuiTableCell sx={{ fontSize: 11, fontWeight: 700, color: aut.ESTADO === 'CHECK_GREEN' ? '#16a34a' : aut.ESTADO === 'CANCEL_RED' ? '#dc2626' : '#d97706' }}>
                          {aut.ESTADO === 'CHECK_GREEN' ? '✓ Autorizado' : aut.ESTADO === 'CANCEL_RED' ? '✗ Cancelado' : '⏳ Pendiente'}
                        </MuiTableCell>
                        <MuiTableCell sx={{ fontSize: 11 }}>{aut.FECHA_AUTORIZACION || '-'}</MuiTableCell>
                      </MuiTableRow>
                    ))}
                  </TableBody>
                </MuiTable>
              ) : (
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  No hay registros de historial de autorización para esta solicitud.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const TableRow = React.memo(({
  row,
  orderedKeys,
  stickyLeftMeta,
  onRowClick,
  onAdminStatusClick,
  hasPartidas,
  isExpanded,
  onToggleExpand,
  renderRowActions,
  labels = {},
  selectable,
  isSelected,
  onSelectRow,
  isFocused,
  compactActions = false,
  actionsColumnWidth,
  onMouseEnter,
  colorMode: colorModeProp = 'full',
  'data-row-index': rowIndex
}) => {
  const confirmarPrecio = row.CONFIRMARPRECIO;
  const canceladoErp = row.CANCELADO_ERP;
  const estatus = row.ESTATUS;
  const disableStatusRowColor = row.__disableStatusRowColor === true;
  const diasRestantes = Number(row.DIASRESTANTES ?? row.DIAS_VENCIMIENTO ?? 0);
  const colorMode = colorModeProp || 'full';

  // ── Fondo de fila según modo ──────────────────────────────────────────────
  let rawBg = renderStatusColor(estatus, confirmarPrecio, canceladoErp);
  let bgColor;
  const isCanceled = estatus === 'Cancelada' || estatus === 'Cancelada ERP' || Number(canceladoErp) === 1;

  if (isCanceled) {
    bgColor = '#fef2f2'; // Fondo rojo muy suave para canceladas
  } else if (disableStatusRowColor || colorMode !== 'full') {
    bgColor = '#ffffff'; // modos border y glow: fila siempre blanca de fondo
  } else {
    bgColor = (rawBg === '#ffffffff' || rawBg === 'transparent') ? '#ffffff' : rawBg;
  }

  // Si el usuario/registro está inactivo, sobreescribimos con gris más marcado
  const isInactivo = row.ACTIVO !== undefined && Number(row.ACTIVO) === 0;
  if (isInactivo) {
    bgColor = '#e2e8f0';
  }

  // ── Borde izquierdo de alerta / estado ───────────────────────────────────
  let leftAlertBorder = '4px solid transparent';
  const isPaid = estatus === 'Pagada';

  if (disableStatusRowColor) {
    leftAlertBorder = '4px solid transparent';
  } else if (colorMode === 'border') {
    // En modo borde: todo el código de color va en el borde izquierdo
    const borderC = renderStatusBorderColor(estatus, canceladoErp);
    leftAlertBorder = `4px solid ${borderC}`;
  } else if (isCanceled) {
    leftAlertBorder = '4px solid #D32F2F';
  } else if (Number(confirmarPrecio) === 1) {
    leftAlertBorder = '4px solid #4caf50';
  } else if (!isPaid && (diasRestantes < 0 || Number(confirmarPrecio) === 0)) {
    leftAlertBorder = '4px solid #FF9800';
  }

  const statusBorderColor = colorMode === 'border' ? renderStatusBorderColor(estatus, canceladoErp) : null;
  const underlineColor = (!disableStatusRowColor && colorMode === 'underline') ? renderStatusBorderColor(estatus, canceladoErp) : null;

  return (
    <>
      <StyledTableRow
        bgColor={isFocused && !isInactivo ? '#f1f5f9' : bgColor}
        statusBorderColor={statusBorderColor}
        underlineColor={underlineColor}
        data-row-index={rowIndex}
        onMouseEnter={onMouseEnter}
        onClick={(e) => {
          const target = e.target;
          if (target.closest('button') || target.closest('a') || target.closest('input') || target.closest('.MuiCheckbox-root')) return;
          
          const selection = window.getSelection().toString();
          if (selection && selection.length > 0) return;

          if (onRowClick) onRowClick(row);
        }}
        sx={{
          ...(isSelected && !isFocused && {
            '& td': { backgroundColor: '#eff6ff !important' },
            boxShadow: 'inset 3px 0 0 #3b82f6',
          }),
          ...(isFocused && !isInactivo && {
            boxShadow: 'inset 4px 0 0 #0655A5',
            '& td': { backgroundColor: '#f1f5f9 !important' }
          }),
          ...(isInactivo && {
            '& td:not(:has(.MuiChip-root))': { color: '#94a3b8 !important' },
            '& td .MuiTypography-root:not(.MuiChip-label)': { color: '#94a3b8 !important' },
          })
        }}
      >
        {selectable && (
          <MuiTableCell
            sx={{
              ...(stickyLeftMeta && stickyLeftMeta['__select__'] ? stickyCellSx(stickyLeftMeta['__select__']) : {}),
              width: 38, minWidth: 38, maxWidth: 38,
              p: '0 !important',
              textAlign: 'center',
              borderLeft: leftAlertBorder,
            }}
          >
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={(e) => onSelectRow(e, String(getRowKey(row)))}
            />
          </MuiTableCell>
        )}

        {/* Columna: Expand toggle */}
        {hasPartidas && (
          <MuiTableCell
            sx={{
              ...(stickyLeftMeta && stickyLeftMeta['__expand__'] ? stickyCellSx(stickyLeftMeta['__expand__']) : {}),
              width: 38, minWidth: 38, maxWidth: 38,
              p: '0 !important',
              textAlign: 'center',
              borderLeft: !selectable ? leftAlertBorder : 'none',
            }}
          >
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleExpand(); }} sx={{ color: 'text.secondary', p: 0.35 }}>
              {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          </MuiTableCell>
        )}

        {orderedKeys.map((key, idx) => {
          const val = row[key];
          const stickyMeta = stickyLeftMeta?.[key];
          const isPinned = !!stickyMeta?.isPinned;
          const type = getColumnType(key);
          const cellContent = formatCellValue(key, val);
          let rendered = null;

          // Si no hay hasPartidas ni selectable, el leftAlertBorder debe ir en la primera celda visible
          const needsLeftBorder = !hasPartidas && !selectable && idx === 0;

          if (key === 'FOLIO') {
            rendered = (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#111827', userSelect: 'text' }}>
                  {cellContent}
                </Typography>
                {Number(confirmarPrecio) === 0 && <Tooltip title="Confirmar Precio"><span><InfoOutlinedIcon sx={{ fontSize: 16, color: '#FF9800' }} /></span></Tooltip>}
                {canceladoErp === 1 && <Tooltip title="Cancelado en ERP"><span><InfoOutlinedIcon sx={{ fontSize: 16, color: '#D32F2F' }} /></span></Tooltip>}
              </Box>
            );
          } else if (key === 'PAGOSAUT_OC_EMPRESA') {
            rendered = <PagosAutOcEmpresaCell row={row} />;
          } else if (key === 'PAGOSAUT_CENTRO_DEPTO') {
            rendered = <PagosAutCentroCell row={row} />;
          } else if (key === 'PAGOSAUT_COMPRADOR_FECHA') {
            rendered = <PagosAutCompradorFechaCell row={row} />;
          } else if (key === 'PAGOSAUT_IMPORTE') {
            rendered = <PagosAutImporteCell row={row} />;
          } else if (key === 'PAGOSAUT_SEGUIMIENTO') {
            rendered = <PagosAutSeguimientoCell row={row} />;
          } else if (key === 'OC_REQUI') {
            const requi = pickFirst(row, ['REQUI']) || '-';
            const empresa = pickFirst(row, ['EMPRESA', 'NOMBRE_EMPRESA']);
            const folio = pickFirst(row, ['FOLIO', 'FOLIO_OC', 'ORDEN']);
            const nota = pickFirst(row, ['NOTAS', 'NOTAS_REQUI', 'NOTA']);
            rendered = (
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <Tooltip title={folio || ''} arrow disableInteractive>
                    <Typography
                      sx={{
                        fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)',
                        color: '#0f172a',
                        fontWeight: 900,
                        lineHeight: 1.12,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                      }}
                    >
                      {folio || '-'}
                    </Typography>
                  </Tooltip>
                  <NotaRequiChip nota={nota} empresa={empresa} />
                </Box>
                <Tooltip title={`Req: ${requi}`} arrow disableInteractive>
                  <Typography
                    sx={{
                      mt: 0.1,
                      fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                      color: '#64748b',
                      fontWeight: 700,
                      lineHeight: 1.15,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Req: {requi}
                  </Typography>
                </Tooltip>
                {empresa && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      mt: 0.22,
                      px: 0.55,
                      py: 0,
                      borderRadius: '6px',
                      border: '1px solid transparent',
                      bgcolor: '#eef6ff',
                      color: '#005eb8',
                      fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                      fontWeight: 900,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      ...getEmpresaChipSx(empresa),
                    }}
                    title={empresa}
                  >
                    {getEmpresaChipLabel(empresa)}
                  </Box>
                )}
              </Box>
            );
          } else if (key === 'USUARIO_USUARIO_ID') {
            const userName = pickFirst(row, ['USER_NAME', 'USERNAME']) || 'N/D';
            const usuarioIdVal = pickFirst(row, ['USUARIOID', 'ID']) || 'N/D';
            rendered = (
              <Box sx={{ minWidth: 0, py: 0.15 }}>
                <Tooltip title={userName} arrow disableInteractive>
                  <Typography
                    sx={{
                      fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)',
                      color: '#0f172a',
                      fontWeight: 650,
                      lineHeight: 1.15,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {userName}
                  </Typography>
                </Tooltip>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    mt: 0.2,
                    px: 0.55,
                    py: 0.05,
                    borderRadius: '3px',
                    bgcolor: '#f1f5f9',
                    color: '#475569',
                    fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                    fontWeight: 700,
                    border: '1px solid #cbd5e1',
                    lineHeight: 1.1,
                  }}
                >
                  ID: {usuarioIdVal}
                </Box>
              </Box>
            );
          } else if (key === 'EMPRESAS_ASIGNADAS') {
            const rawEmpresas = pickFirst(row, ['NOMBRE_EMPRESA', 'EMPRESA', 'EMPRESAS_ASIGNADAS']) || '';
            const empresasList = String(rawEmpresas).split(',').map(e => e.trim()).filter(Boolean);

            rendered = (
              <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap sx={{ py: 0.2, maxWidth: 220 }}>
                {empresasList.length > 0 ? (
                  empresasList.map(emp => (
                    <Box
                      key={emp}
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        px: 0.65,
                        py: 0.2,
                        borderRadius: '6px',
                        border: '1px solid transparent',
                        fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                        fontWeight: 900,
                        whiteSpace: 'nowrap',
                        ...getEmpresaChipSx(emp),
                      }}
                      title={emp}
                    >
                      {getEmpresaChipLabel(emp)}
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.08vw, 0.68rem)', color: '#94a3b8' }}>Sin Empresa</Typography>
                )}
              </Stack>
            );
          } else if (key === 'TESORERO_ID_FOLIO_EMPRESA') {
            const idSol = pickFirst(row, ['ID_SOLICITUD', 'ID']);
            const folio = pickFirst(row, ['FOLIO', 'FOLIO_OC']);
            const empresa = pickFirst(row, ['NOMBRE_EMPRESA', 'EMPRESA']);
            rendered = (
              <Box sx={{ minWidth: 0, py: 0.2 }}>
                <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)', color: '#0f172a', fontWeight: 650, lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                  {folio || idSol || 'N/A'}
                </Typography>
                {idSol && folio && (
                  <Typography sx={{ mt: 0.05, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#64748b', fontWeight: 400, lineHeight: 1.05, whiteSpace: 'nowrap' }}>
                    ID Sol: {idSol}
                  </Typography>
                )}
                {empresa && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      mt: 0.3,
                      px: 0.65,
                      py: 0.1,
                      borderRadius: '6px',
                      border: '1px solid transparent',
                      fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                      fontWeight: 900,
                      whiteSpace: 'nowrap',
                      ...getEmpresaChipSx(empresa),
                    }}
                    title={empresa}
                  >
                    {getEmpresaChipLabel(empresa)}
                  </Box>
                )}
              </Box>
            );
          } else if (key === 'TESORERO_SOLICITANTE_RFC') {
            const solicitante = pickFirst(row, ['SOLICITANTE']);
            const rfc = pickFirst(row, ['RFC']);
            rendered = (
              <Box sx={{ minWidth: 0, py: 0.1 }}>
                <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)', color: '#0f172a', fontWeight: 650, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }} title={solicitante}>
                  {solicitante || 'N/D'}
                </Typography>
                <Typography sx={{ mt: 0.05, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#64748b', fontWeight: 400, lineHeight: 1.05, whiteSpace: 'nowrap' }}>
                  {rfc ? `RFC: ${rfc}` : 'Sin RFC'}
                </Typography>
              </Box>
            );
          } else if (key === 'TESORERO_MONTO_ESTATUS') {
            const divisa = String(pickFirst(row, ['DIVISA', 'MONEDA', 'moneda']) || 'MXN').toUpperCase().trim();
            const esUsd = divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR';
            const rawVal = pickFirst(row, ['MONTO', 'TOTAL', 'MONTOSOLICITADO', 'pago']);
            const numVal = Number(String(rawVal ?? 0).replace(/[^0-9.-]/g, '')) || 0;
            const montoVal = esUsd 
              ? `$${numVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`
              : `${formatCellValue('MONTO', numVal)} MXN`;
            const metodo = pickFirst(row, ['METODOPAGO']);
            const estatus = pickFirst(row, ['ESTATUS']) || 'Pagada';
            const isCancel = String(estatus).toLowerCase().includes('cancel');

            rendered = (
              <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.1 }}>
                <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.10vw, 0.70rem)', fontWeight: 700, color: esUsd ? '#0284c7' : '#0f172a', lineHeight: 1.15 }}>
                  {montoVal}
                </Typography>
                {metodo && (
                  <Typography sx={{ mt: 0.05, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#64748b', fontWeight: 400, lineHeight: 1.05 }}>
                    {metodo}
                  </Typography>
                )}
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignSelf: 'flex-start',
                    mt: 0.2,
                    px: 0.5,
                    py: 0,
                    borderRadius: '4px',
                    border: '1px solid transparent',
                    bgcolor: isCancel ? '#fee2e2' : '#dcfce7',
                    color: isCancel ? '#991b1b' : '#15803d',
                    borderColor: isCancel ? '#fca5a5' : '#86efac',
                    fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.15,
                  }}
                >
                  {estatus}
                </Box>
              </Box>
            );
          } else if (key === 'TESORERO_COMPROBANTE_REF') {
            const comp = pickFirst(row, ['COMPROBANTE', 'IMAGEN']);
            const ref = pickFirst(row, ['REFERENCIA']);
            rendered = (
              <Box sx={{ minWidth: 0, py: 0.2 }}>
                <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)', color: '#0f172a', fontWeight: 650, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }} title={comp}>
                  {comp || 'Sin comprobante'}
                </Typography>
                {ref && (
                  <Typography sx={{ mt: 0.05, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#64748b', fontWeight: 400, lineHeight: 1.05, whiteSpace: 'nowrap' }}>
                    Ref: {ref}
                  </Typography>
                )}
              </Box>
            );
          } else if (key === 'OC_REQUI' || key === 'PAGOSAUT_OC_EMPRESA') {
            rendered = <PagosAutOcEmpresaCell row={row} />;
          } else if (key === 'FECHA_SOLICITANTE') {
            const fechaVal = formatCellValue('FECHA', pickFirst(row, ['FECHASOLICITUD', 'FECHA_SOLICITUD_PAGO', 'FECHA_SOLICITUD', 'FECHA', 'FECHA_OC', 'FECHA_EMISION', 'FECHA_CREACION', 'FECHACREACION', 'FECHA_REQUISICION']));
            const reqVal = pickFirst(row, ['REQUISITOR', 'SOLICITANTE']) || '-';
            rendered = (
              <CompactInfoCell
                primary={fechaVal}
                secondary={`Req: ${reqVal}`}
                title={fechaVal}
                secondaryTitle={`Requisitor: ${reqVal}`}
              />
            );
          } else if (key === 'PROVEEDOR_COMPRADOR') {
            const proveedor = pickFirst(row, ['PROVEEDOR']);
            const comprador = pickFirst(row, ['COMPRADOR', 'SOLICITANTE']) || '-';
            rendered = (
              <CompactInfoCell
                primary={proveedor || '-'}
                secondary={`Comp: ${comprador}`}
                title={proveedor}
                secondaryTitle={`Comprador: ${comprador}`}
                maxWidth={{ xs: 160, md: 220, lg: 320 }}
              />
            );
          } else if (key === 'TOTAL_DIVISA') {
            rendered = <TotalDivisaCell row={row} />;
          } else if (key === 'MONTO_CONFIRMADO_PRECIO') {
            const montoVal = formatCellValue('MONTOSOLICITADO', pickFirst(row, ['MONTOSOLICITADO', 'MONTO_SOLICITADO']));
            const confirmado = Number(pickFirst(row, ['CONFIRMARPRECIO'])) === 1;
            rendered = (
              <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.2 }}>
                <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)', fontWeight: 650, color: '#0f172a', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                  {montoVal}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignSelf: 'flex-start',
                    mt: 0.3,
                    px: 0.6,
                    py: 0.05,
                    borderRadius: '6px',
                    border: '1px solid transparent',
                    bgcolor: confirmado ? '#dcfce7' : '#fef3c7',
                    color: confirmado ? '#15803d' : '#b45309',
                    borderColor: confirmado ? '#86efac' : '#fde68a',
                    fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.25,
                  }}
                >
                  {confirmado ? 'Precio Confirmado' : 'Falta Confirmar'}
                </Box>
              </Box>
            );
          } else if (key === 'SEGURIDAD_2FA') {
            const allowsPass = row?.permitir_password !== undefined ? Number(row.permitir_password) === 1 : true;
            const allowsMs = row?.permitir_microsoft !== undefined ? Number(row.permitir_microsoft) === 1 : true;
            const is2faActive = Number(row?.requiere_2fa) === 1;

            rendered = (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                {/* Badge de Contraseña / 2FA */}
                {allowsPass ? (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.4,
                      px: 0.7,
                      py: 0.2,
                      borderRadius: '6px',
                      border: '1px solid',
                      bgcolor: is2faActive ? '#ecfdf5' : '#f1f5f9',
                      color: is2faActive ? '#047857' : '#334155',
                      borderColor: is2faActive ? '#a7f3d0' : '#cbd5e1',
                      fontSize: 'clamp(0.56rem, 0.52rem + 0.08vw, 0.66rem)',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                    title={is2faActive ? 'Acceso con Contraseña + 2FA requerido' : 'Acceso directo con Contraseña'}
                  >
                    <span>{is2faActive ? '🔒' : '🔑'}</span>
                    <span>{is2faActive ? '2FA' : 'Pass'}</span>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.4,
                      px: 0.6,
                      py: 0.2,
                      borderRadius: '6px',
                      border: '1px dashed #cbd5e1',
                      bgcolor: '#f8fafc',
                      color: '#94a3b8',
                      fontSize: 'clamp(0.54rem, 0.50rem + 0.08vw, 0.64rem)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                    title="Acceso con contraseña local deshabilitado"
                  >
                    <span>🚫</span>
                    <span>Sin Pass</span>
                  </Box>
                )}

                {/* Badge de Microsoft Office 365 */}
                {allowsMs && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.4,
                      px: 0.7,
                      py: 0.2,
                      borderRadius: '6px',
                      border: '1px solid #bfdbfe',
                      bgcolor: '#eff6ff',
                      color: '#1d4ed8',
                      fontSize: 'clamp(0.56rem, 0.52rem + 0.08vw, 0.66rem)',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                    title="Acceso permitido con Microsoft Office 365"
                  >
                    <span>🏢</span>
                    <span>Office</span>
                  </Box>
                )}
              </Box>
            );
          } else if (key === 'NOTAS_PARTIDAS_ECO') {
            const notasPart = pickFirst(row, ['NOTASPARTIDAS', 'NOTAS_PARTIDAS', 'NOTAS']);
            const numEco = pickFirst(row, ['NUMERO_ECONOMICO', 'NUMERO_ECO', 'ECONOMICO', 'ECO', 'NUMEROECONOMICO', 'ECO_EQUIPO', 'EQUIPO']);
            rendered = (
              <Box sx={{ minWidth: 0, py: 0.1 }}>
                <Typography 
                  sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)', color: '#0f172a', fontWeight: 650, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }} 
                  title={notasPart || 'Sin notas de partidas'}
                >
                  {notasPart || 'Sin notas de partidas'}
                </Typography>
                {numEco && String(numEco).trim() !== '' ? (
                  <Typography 
                    sx={{ 
                      mt: 0.05, 
                      fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', 
                      color: '#0369a1', 
                      fontWeight: 500, 
                      lineHeight: 1.1, 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      maxWidth: 240 
                    }} 
                    title={`No. Económico: ${numEco}`}
                  >
                    No. Económico: {numEco}
                  </Typography>
                ) : (
                  <Typography sx={{ mt: 0.05, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#94a3b8', fontWeight: 400, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                    Sin No. Económico
                  </Typography>
                )}
              </Box>
            );
          } else if (key === 'NOTAS_REQUISICION') {
            const notasReq = pickFirst(row, ['NOTASREQUISICION', 'NOTAS_REQUISICION', 'NOTAS_REQUI']);
            rendered = (
              <Box sx={{ minWidth: 0, py: 0.1 }}>
                <Typography 
                  sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)', color: '#475569', fontWeight: 600, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }} 
                  title={notasReq || 'Sin notas'}
                >
                  {notasReq || 'Sin notas'}
                </Typography>
              </Box>
            );
          } else if (key === 'CENTRO_DEPTO') {
            const numCC = pickFirst(row, ['NUMEROCC', 'NUMERO_CC', 'NUMERO_CENTRO_COSTO', 'CC']);
            const nombreCC = pickFirst(row, ['CentroCosto', 'CENTROCOSTO', 'CENTROCOSTOS', 'NOMBRE_CC']);
            const depto = pickFirst(row, ['Departamento', 'DEPARTAMENTO', 'NOMBRE_DEPARTAMENTO']);

            rendered = (
              <Box sx={{ minWidth: 0, py: 0.1 }}>
                {numCC ? (
                  <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)', color: '#0f172a', fontWeight: 650, lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                    {numCC}
                  </Typography>
                ) : null}
                <Typography sx={{ fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#475569', fontWeight: 500, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }} title={nombreCC || 'Sin CC'}>
                  {nombreCC || (numCC ? '' : 'Sin CC')}
                </Typography>
                {depto && (
                  <Typography sx={{ mt: 0.05, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#64748b', fontWeight: 400, lineHeight: 1.05, whiteSpace: 'nowrap' }}>
                    Depto: {depto}
                  </Typography>
                )}
              </Box>
            );
          } else if (key === 'TOTAL_DIVISA') {
            rendered = <TotalDivisaCell row={row} />;
          } else if (key === 'TOTAL_MN') {
            const divisa = String(pickFirst(row, ['DIVISA', 'MONEDA', 'moneda']) || 'MXN').toUpperCase().trim();
            const esMxn = divisa === 'MXN' || divisa === 'MN' || divisa === 'PESOS';
            const rawVal = pickFirst(row, ['MONTOSOLICITADO', 'MONTO_SOLICITADO', 'pago', 'MONTO', 'TOTALOC', 'TOTAL']);
            const numVal = Number(String(rawVal ?? 0).replace(/[^0-9.-]/g, '')) || 0;
            const val = esMxn ? numVal : 0;
            rendered = (
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.48rem + 0.35vw, 0.85rem)', color: val > 0 ? '#0f172a' : '#94a3b8', fontWeight: val > 0 ? 600 : 400, textAlign: val > 0 ? 'right' : 'center', whiteSpace: 'nowrap' }}>
                {val > 0 ? formatCellValue('MONTOSOLICITADO', val) : '-'}
              </Typography>
            );
          } else if (key === 'TOTAL_USD') {
            const divisa = String(pickFirst(row, ['DIVISA', 'MONEDA', 'moneda']) || 'MXN').toUpperCase().trim();
            const esUsd = divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR';
            const rawVal = pickFirst(row, ['MONTOSOLICITADO', 'MONTO_SOLICITADO', 'pago', 'MONTO', 'TOTALOC', 'TOTAL']);
            const numVal = Number(String(rawVal ?? 0).replace(/[^0-9.-]/g, '')) || 0;
            const val = esUsd ? numVal : 0;
            rendered = (
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.48rem + 0.35vw, 0.85rem)', color: val > 0 ? '#0284c7' : '#94a3b8', fontWeight: val > 0 ? 600 : 400, textAlign: val > 0 ? 'right' : 'center', whiteSpace: 'nowrap' }}>
                {val > 0 ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
              </Typography>
            );
          } else if (key === 'TOTAL_EUR') {
            const divisa = String(pickFirst(row, ['DIVISA', 'MONEDA', 'moneda']) || 'MXN').toUpperCase().trim();
            const esEur = divisa === 'EUR' || divisa === 'EUROS' || divisa === 'EURO';
            const rawVal = pickFirst(row, ['MONTOSOLICITADO', 'MONTO_SOLICITADO', 'pago', 'MONTO', 'TOTALOC', 'TOTAL']);
            const numVal = Number(String(rawVal ?? 0).replace(/[^0-9.-]/g, '')) || 0;
            const val = esEur ? numVal : 0;
            rendered = (
              <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)', color: val > 0 ? '#7c3aed' : '#94a3b8', fontWeight: val > 0 ? 650 : 400, textAlign: val > 0 ? 'right' : 'center', whiteSpace: 'nowrap' }}>
                {val > 0 ? `€${val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
              </Typography>
            );
          } else if (key === 'FECHA_REPORTE_PAGO') {
            const fechaStr = formatCellValue('FECHASOLICITUD', pickFirst(row, ['FECHA_REPORTE_PAGO', 'FECHA_SOLICITUD_PAGO', 'FECHA_REPORTE']));
            const veces = Number(row.VECES_REPORTADO || 0);

            rendered = (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 0.15 }}>
                <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.12vw, 0.72rem)', fontWeight: 650, color: '#0f172a', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                  {fechaStr || '-'}
                </Typography>
                {veces > 1 && (
                  <Chip
                    label={`${veces} veces`}
                    size="small"
                    sx={{
                      mt: 0.3,
                      height: 16,
                      fontSize: 'clamp(0.58rem, 0.56rem + 0.10vw, 0.70rem)',
                      fontWeight: 800,
                      bgcolor: '#eff6ff',
                      color: '#1d4ed8',
                      border: '1px solid #bfdbfe',
                      px: 0.4,
                      '& .MuiChip-label': { px: 0.5 }
                    }}
                  />
                )}
              </Box>
            );
          } else if (key === 'EXHIBIT' || key === 'EXHIBICION') {
            const rawExhibit = pickFirst(row, ['EXHIBIT', 'EXHIBICION', 'exhibit', 'exhibicion']);
            const exhibitVal = rawExhibit ? String(rawExhibit).replace(/^Solicitud\s*/i, '').trim() : '';
            rendered = (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 0.1 }}>
                {exhibitVal && exhibitVal !== '-' && exhibitVal !== '0' ? (
                  <Chip
                    label={exhibitVal}
                    size="small"
                    sx={{
                      height: 'clamp(18px, 1.4vw, 22px)',
                      fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)',
                      fontWeight: 800,
                      bgcolor: '#f1f5f9',
                      color: '#0369a1',
                      border: '1px solid #bae6fd',
                      borderRadius: '6px',
                      px: 0.4,
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                ) : (
                  <Typography sx={{ fontSize: 'clamp(0.65rem, 0.60rem + 0.1vw, 0.75rem)', color: '#94a3b8', fontWeight: 600 }}>
                    -
                  </Typography>
                )}
              </Box>
            );
          } else if (key === 'MONTO_RESTANTE') {
            rendered = (
              <CompactInfoCell
                primary={formatCellValue('MONTOSOLICITADO', pickFirst(row, ['MONTOSOLICITADO', 'MONTO_SOLICITADO']))}
                secondary={`Restante: ${formatCellValue('PENDIENTE', pickFirst(row, ['PENDIENTE', 'RESTANTE']))}`}
              />
            );
          } else if (key === 'FACTURA_COMPACTA') {
            rendered = (
              <FacturaCompactCell
                value={pickFirst(row, ['FOLIO_FACTURA', 'FACTURA'])}
                pago={pickFirst(row, ['FECHAPAGO', 'FECHA_PAGO']) ? formatCellValue('FECHAPAGO', pickFirst(row, ['FECHAPAGO', 'FECHA_PAGO'])) : ''}
              />
            );
          } else if (key === 'FACTURA_PAGO') {
            rendered = <FacturaEconomicoCell row={row} />;
          } else if (key === 'MONTOS_RESUMEN') {
            rendered = <SolicitudSaldoCell row={row} />;
          } else if (key === 'SOLICITUD_RESUMEN') {
            rendered = <AdminPaymentStatusCell type="solicitud" row={row} onClick={onAdminStatusClick} />;
          } else if (key === 'REPORTE_SOLICITADO_RESUMEN') {
            rendered = <AdminPaymentStatusCell type="reporte" row={row} onClick={onAdminStatusClick} />;
          } else if (key === 'ENTRADA_RESUMEN') {
            rendered = <AdminPaymentStatusCell type="entrada" row={row} onClick={onAdminStatusClick} />;
          } else if (key === 'PAGO_ERP_RESUMEN') {
            rendered = <AdminPaymentStatusCell type="comprobante" row={row} onClick={onAdminStatusClick} />;
          } else if (key === 'PAGO_RESUMEN') {
            rendered = <AdminPaymentStatusCell type="pago" row={row} onClick={onAdminStatusClick} />;
          } else if (key === 'ESTATUS_COMPACTO' || key === 'ESTATUS' || key === 'ESTADO_CORRECCION') {
            const statusVal = pickFirst(row, ['ESTATUS', 'ESTATUS_COMPACTO']) || val;
            rendered = renderEstatusChip(statusVal);
          } else if (key.startsWith('AUTNIV') || key === 'CONFIRMARPRECIO') {
            const authName = key.startsWith('AUTNIV') ? row[`${key}_NOMBRE`] : '';
            const authDate = key.startsWith('AUTNIV') ? row[`${key}_FECHA`] : '';
            const authStatus = renderAuthStatus(val, row.MOTIVOCANCELACION);
            rendered = authName ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.12, minWidth: 0, lineHeight: 1 }}>
                <Tooltip title={authName} arrow disableInteractive>
                  <span>
                    <Typography
                      sx={{
                        maxWidth: 86,
                        color: '#475569',
                        fontSize: 9,
                        fontWeight: 800,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {authName}
                    </Typography>
                  </span>
                </Tooltip>
                <Box sx={{ lineHeight: 0, '& svg': { fontSize: 18 } }}>
                  {authStatus}
                </Box>
                {authDate && (
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: 8.7,
                      fontWeight: 800,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {authDate}
                  </Typography>
                )}
              </Box>
            ) : authStatus;
          } else if (key === 'CANCELADO_ERP') {
            rendered = renderCanceladoERP(val);
          } else if (key.toUpperCase() === 'DOCUMENTACION' || key.toUpperCase() === 'DOCS') {
            const hasOc = Number(row.HAS_OC || row.has_oc || 0) > 0;
            const hasFac = Number(row.HAS_FAC || row.has_fac || 0) > 0;
            const hasCb = Number(row.HAS_CB || row.has_cb || 0) > 0;

            rendered = (
              <Stack direction="row" spacing={0.75} justifyContent="center">
                <Tooltip title={hasOc ? 'Orden de Compra Adjunta' : 'FALTA: Orden de Compra'}>
                  <span>
                    <Chip
                      label="OC"
                      size="small"
                      icon={hasOc ? <CheckCircleIcon style={{ fontSize: 13, color: '#166534' }} /> : <ErrorOutlineIcon style={{ fontSize: 13, color: '#991b1b' }} />}
                      sx={{
                        fontSize: '0.65rem', fontWeight: 900, height: 20, px: 0.5,
                        bgcolor: hasOc ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                        color: hasOc ? '#166534' : '#991b1b',
                        border: '1px solid',
                        borderColor: hasOc ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                        '& .MuiChip-icon': { ml: 0.5, mr: -0.2 }
                      }}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={hasFac ? 'Factura Adjunta' : 'FALTA: Factura'}>
                  <span>
                    <Chip
                      label="FAC"
                      size="small"
                      icon={hasFac ? <CheckCircleIcon style={{ fontSize: 13, color: '#166534' }} /> : <ErrorOutlineIcon style={{ fontSize: 13, color: '#991b1b' }} />}
                      sx={{
                        fontSize: '0.65rem', fontWeight: 900, height: 20, px: 0.5,
                        bgcolor: hasFac ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                        color: hasFac ? '#166534' : '#991b1b',
                        border: '1px solid',
                        borderColor: hasFac ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                        '& .MuiChip-icon': { ml: 0.5, mr: -0.2 }
                      }}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={hasCb ? 'Carátula/CB Adjunta' : 'FALTA: Carátula/CB'}>
                  <span>
                    <Chip
                      label="CB"
                      size="small"
                      icon={hasCb ? <CheckCircleIcon style={{ fontSize: 13, color: '#166534' }} /> : <ErrorOutlineIcon style={{ fontSize: 13, color: '#991b1b' }} />}
                      sx={{
                        fontSize: '0.65rem', fontWeight: 900, height: 20, px: 0.5,
                        bgcolor: hasCb ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                        color: hasCb ? '#166534' : '#991b1b',
                        border: '1px solid',
                        borderColor: hasCb ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                        '& .MuiChip-icon': { ml: 0.5, mr: -0.2 }
                      }}
                    />
                  </span>
                </Tooltip>
              </Stack>
            );
          } else if (key === 'NOMBRE_EMPRESA' || key === 'EMPRESA') {
            const rawEmp = val || pickFirst(row, ['NOMBRE_EMPRESA', 'EMPRESA']) || '';
            const empList = String(rawEmp).split(',').map(e => e.trim()).filter(Boolean);

            rendered = (
              <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap sx={{ py: 0.2, maxWidth: 220 }}>
                {empList.length > 0 ? (
                  empList.map(emp => (
                    <Box
                      key={emp}
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        px: 0.65,
                        py: 0.2,
                        borderRadius: '6px',
                        border: '1px solid transparent',
                        fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                        fontWeight: 900,
                        whiteSpace: 'nowrap',
                        ...getEmpresaChipSx(emp),
                      }}
                      title={emp}
                    >
                      {getEmpresaChipLabel(emp)}
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.08vw, 0.68rem)', color: '#94a3b8' }}>Sin Empresa</Typography>
                )}
              </Stack>
            );
          } else if (key === 'EMPRESA_DIVISA') {
            const empresaVal = pickFirst(row, ['Empresa', 'EMPRESA', 'NOMBRE_EMPRESA']) || '-';
            const divisaRaw = pickFirst(row, ['Divisa', 'DIVISA', 'NOMBRE_DIVISA']);
            const divisaVal = divisaRaw && String(divisaRaw).trim() ? String(divisaRaw).trim() : null;
            rendered = (
              <Box sx={{ minWidth: 0, py: 0.15 }}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    px: 0.65,
                    py: 0.2,
                    borderRadius: '6px',
                    border: '1px solid transparent',
                    fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    ...getEmpresaChipSx(empresaVal),
                  }}
                  title={empresaVal}
                >
                  {getEmpresaChipLabel(empresaVal)}
                </Box>
                <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.08vw, 0.68rem)', color: '#64748b', mt: 0.2, lineHeight: 1.2 }}>
                  Divisa: {divisaVal || 'N/A'}
                </Typography>
              </Box>
            );
          } else if (key === 'AUTORIZADORES' || key === 'TESOREROS') {
            const isTesoreroGroup = key === 'TESOREROS';

            // Los datos vienen serializados como JSON desde NivelAut.js
            let groupItems = [];
            try {
              const parsed = JSON.parse(val || '[]');
              if (Array.isArray(parsed)) groupItems = parsed;
            } catch (e) {
              groupItems = [];
            }

            const getAccentColorGroup = (name) => {
              const palettes = [
                { bar: '#3b82f6', nameTxt: '#1e40af', badgeBg: '#eff6ff', badgeBorder: '#bfdbfe', badgeTxt: '#1e40af' },
                { bar: '#10b981', nameTxt: '#065f46', badgeBg: '#ecfdf5', badgeBorder: '#6ee7b7', badgeTxt: '#065f46' },
                { bar: '#f59e0b', nameTxt: '#92400e', badgeBg: '#fffbeb', badgeBorder: '#fde68a', badgeTxt: '#92400e' },
                { bar: '#8b5cf6', nameTxt: '#5b21b6', badgeBg: '#f5f3ff', badgeBorder: '#ddd6fe', badgeTxt: '#5b21b6' },
                { bar: '#f97316', nameTxt: '#9a3412', badgeBg: '#fff7ed', badgeBorder: '#fed7aa', badgeTxt: '#9a3412' },
                { bar: '#ec4899', nameTxt: '#9d174d', badgeBg: '#fdf2f8', badgeBorder: '#fbcfe8', badgeTxt: '#9d174d' },
                { bar: '#0ea5e9', nameTxt: '#075985', badgeBg: '#f0f9ff', badgeBorder: '#bae6fd', badgeTxt: '#075985' },
                { bar: '#14b8a6', nameTxt: '#134e4a', badgeBg: '#f0fdfa', badgeBorder: '#99f6e4', badgeTxt: '#134e4a' },
              ];
              if (!name) return palettes[0];
              let hash = 0;
              for (let c of name) hash = (hash * 31 + c.charCodeAt(0)) & 0xfffffff;
              return palettes[Math.abs(hash) % palettes.length];
            };

            const tesoreroStyleGroup = { bar: '#059669', nameTxt: '#065f46', badgeBg: '#ecfdf5', badgeBorder: '#6ee7b7', badgeTxt: '#065f46' };

            rendered = groupItems.length === 0 ? (
              <Typography sx={{ fontSize: 11, color: '#cbd5e1', fontWeight: 500 }}>—</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, py: 0.1 }}>
                {groupItems.map(({ name, level }) => {
                  const palette = isTesoreroGroup ? tesoreroStyleGroup : getAccentColorGroup(name);
                  return (
                    <Tooltip key={level} title={`${isTesoreroGroup ? 'Tesorero' : `Autorizador`} ${level}: ${name}`} arrow disableInteractive>
                      <span>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.6,
                            minWidth: 0,
                            pl: 0.6,
                            pr: 0.4,
                            py: 0.1,
                            borderLeft: `2.5px solid ${palette.bar}`,
                            borderRadius: '4px',
                            bgcolor: 'rgba(241, 245, 249, 0.6)',
                          }}
                        >
                          <Typography
                            sx={{
                              maxWidth: 72,
                              color: palette.nameTxt,
                              fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
                              fontWeight: 800,
                              lineHeight: 1.1,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {name}
                          </Typography>
                          <Box
                            sx={{
                              px: 0.4,
                              py: 0.05,
                              borderRadius: '3px',
                              bgcolor: palette.badgeBg,
                              border: `1px solid ${palette.badgeBorder}`,
                              color: palette.badgeTxt,
                              fontSize: 7.8,
                              fontWeight: 700,
                              lineHeight: 1,
                              letterSpacing: 0.2,
                            }}
                          >
                            {isTesoreroGroup ? `Tes ${level}` : `Aut ${level}`}
                          </Box>
                        </Box>
                      </span>
                    </Tooltip>
                  );
                })}
              </Box>
            );
          } else if (key === 'ACTIVO') {
            const isActivo = Number(val) === 1 || val === true || val === '1';
            rendered = (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Chip
                  size="small"
                  label={isActivo ? 'ACTIVO' : 'INACTIVO'}
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    borderRadius: '12px',
                    px: 0.8,
                    bgcolor: isActivo ? 'rgba(236, 253, 245, 0.9)' : 'rgba(254, 242, 242, 0.9)',
                    color: isActivo ? '#047857' : '#991b1b',
                    border: '1px solid',
                    borderColor: isActivo ? '#a7f3d0' : '#fecaca',
                  }}
                />
              </Box>
            );
          } else {
            rendered = <CellText text={cellContent} type={type} isPinned={isPinned} />;
          }

          return (
            <MuiTableCell
              key={key}
              sx={{
                ...stickyCellSx(stickyMeta),
                padding: '3px 8px',
                borderLeft: needsLeftBorder ? leftAlertBorder : 'none',
                ...(isCanceled && {
                  opacity: 0.55,
                  '& .MuiTypography-root, & span, & p, & .CellText-root': {
                    textDecoration: 'line-through !important',
                    color: '#94a3b8 !important',
                  }
                }),
                ...(key === 'TESORERO_ID_FOLIO_EMPRESA' ? {
                  width: '120px',
                  minWidth: '100px',
                  maxWidth: '140px',
                  overflow: 'hidden',
                } : {}),
                ...(key === 'ID' || key === 'ACTIVO' ? {
                  textAlign: 'center',
                  width: '80px',
                  minWidth: '65px',
                  maxWidth: '90px',
                } : {}),
                ...(key === 'FACTURA_COMPACTA' ? {
                  width: '150px',
                  minWidth: '130px',
                  maxWidth: '180px',
                  overflow: 'hidden',
                } : {}),
                ...(key === 'FACTURA_PAGO' ? {
                  width: '180px',
                  minWidth: '150px',
                  overflow: 'hidden',
                } : {}),
                ...(key === 'MONTOS_RESUMEN' ? {
                  width: '165px',
                  minWidth: '150px',
                  overflow: 'hidden',
                } : {}),
                ...(key === 'TOTAL_DIVISA' ? {
                  width: '130px',
                  minWidth: '115px',
                  overflow: 'hidden',
                } : {}),
                ...(key === 'CENTRO_DEPTO' ? {
                  width: '220px',
                  minWidth: '170px',
                  overflow: 'hidden',
                } : {}),
                ...(key === 'PROVEEDOR_COMPRADOR' ? {
                  width: '240px',
                  minWidth: '180px',
                  overflow: 'hidden',
                } : {}),
                ...(key === 'FECHA_SOLICITANTE' ? {
                  width: '135px',
                  minWidth: '115px',
                  overflow: 'hidden',
                } : {}),
                ...(key === 'OC_REQUI' ? {
                  width: '115px',
                  minWidth: '95px',
                  overflow: 'hidden',
                } : {}),
                ...(key === 'ESTATUS' || key === 'ESTATUS_COMPACTO' ? {
                  width: '150px',
                  minWidth: '120px',
                  textAlign: 'center',
                } : {}),
                ...((key === 'EXHIBIT' || key === 'EXHIBICION') ? {
                  '& .MuiTypography-root': {
                    fontSize: '0.72rem !important',
                    fontWeight: '600 !important',
                    color: '#475569 !important',
                    letterSpacing: '0.01em',
                  }
                } : {}),
              }}
            >
              {rendered}
            </MuiTableCell>
          );
        })}
        {renderRowActions && (
          <MuiTableCell sx={{
            textAlign: 'center',
            whiteSpace: 'nowrap',
            padding: compactActions ? '2px 4px' : undefined,
            width: compactActions ? actionsColumnWidth : undefined,
            minWidth: compactActions ? actionsColumnWidth : undefined,
            maxWidth: compactActions ? actionsColumnWidth : undefined,
            position: 'sticky',
            right: 0,
            zIndex: 10,
            backgroundColor: 'inherit',
            backgroundImage: 'inherit',
            boxShadow: 'none',
            borderLeft: 'none',
          }}>
            {renderRowActions(row)}
          </MuiTableCell>
        )}
      </StyledTableRow>

      {/* Partidas Nested Table */}
      {hasPartidas && (
        <MuiTableRow sx={{ position: 'relative', zIndex: 0, backgroundColor: '#f8fafc', '& > td': { borderBottom: isExpanded ? '1px solid #eaebec' : 'none' } }}>
          <MuiTableCell style={{ paddingBottom: 0, paddingTop: 0, border: 'none' }} colSpan={orderedKeys.length + (renderRowActions ? 1 : 0) + (selectable ? 1 : 0) + 1}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <PartidasDetailPanel row={row} labels={labels} />
            </Collapse>
          </MuiTableCell>
        </MuiTableRow>
      )}
    </>
  );
}, (prevProps, nextProps) => {
  // Comparador optimizado: evitar re-render si los datos relevantes de la fila no cambiaron
  if (prevProps.row !== nextProps.row) return false;
  if (prevProps.isExpanded !== nextProps.isExpanded) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isFocused !== nextProps.isFocused) return false;
  if (prevProps.colorMode !== nextProps.colorMode) return false;
  if (prevProps.orderedKeys !== nextProps.orderedKeys) {
    if (prevProps.orderedKeys?.length !== nextProps.orderedKeys?.length) return false;
    for (let i = 0; i < prevProps.orderedKeys.length; i++) {
      if (prevProps.orderedKeys[i] !== nextProps.orderedKeys[i]) return false;
    }
  }
  return true;
});
